import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Keyboard,
  Presentation,
} from "lucide-react";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LineChart,
  Line,
  Legend,
  Treemap,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
} from "recharts";
import { fmtDays, fmtYears, fmtMoney, fmtPct } from "@/lib/chart-utils";
import {
  mortgageOverActiveYears,
  aggregateBudgetIntoSectors,
  SECTOR_ORDER,
  SECTOR_RESTITUTION_DEFAULTS,
} from "@/lib/simulator";

/**
 * 7-slide full-screen presentation for VITAE-PUBLICA™ dashboards.
 * Keyboard: ← / → to navigate, Escape to close, Home/End to jump.
 */

const SLIDE_COUNT = 7;

function SlideShell({ idx, total, title, subtitle, children, testId }) {
  return (
    <div
      className="flex h-full w-full flex-col justify-center px-10 py-8 sm:px-16 sm:py-12"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-brand-gold">
        <Presentation className="h-4 w-4" />
        Slide {idx + 1}/{total}
      </div>
      <h1 className="mt-2 font-judicial text-3xl font-black leading-tight text-white sm:text-5xl">
        {title}
      </h1>
      {subtitle && (
        <div className="mt-2 max-w-3xl text-sm italic text-white/60 sm:text-base">
          {subtitle}
        </div>
      )}
      <div className="mt-8 flex-1">{children}</div>
    </div>
  );
}

function BigKpi({ label, value, accent }) {
  return (
    <div
      className="rounded-lg border-2 p-6"
      style={{
        borderColor: accent,
        backgroundColor: `${accent}15`,
      }}
    >
      <div className="text-[10px] uppercase tracking-widest text-white/70">
        {label}
      </div>
      <div
        className="mt-2 font-mono text-5xl font-black sm:text-6xl"
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

export default function PresentationMode({ project, analysis, onClose }) {
  const [slide, setSlide] = useState(0);
  const results = analysis?.results || {};
  const summary = results.summary || {};
  const currency = summary.currency || "UM";
  const profile = results.profiles?.[0] || {};
  const ind = profile.individual || {};
  const national = results.national || {};
  const debt = results.debt || {};
  const legal = results.legal_analysis;
  const budgetLines = results.aggregated_budget_lines || [];

  // Slide 5 data (intergen) — use manual defaults if debt is absent
  const publicDebt =
    debt.dette_publique_totale || summary.total_debt_aggregated || 0;
  const populationTotal = Math.max(
    (debt.population_active || 1_000_000) * 3,
    1_000_000
  );
  const salaireJournalierMoyen =
    debt.salaire_journalier_moyen ||
    national.salaire_journalier_moyen ||
    ind.salaire_journalier ||
    0;

  const mortgageTimeline = useMemo(
    () =>
      publicDebt && salaireJournalierMoyen
        ? mortgageOverActiveYears({
            publicDebt,
            populationTotal,
            salaireJournalierMoyen,
            debtGrowthRateAnnual: 0.05,
            activeYears: 40,
          })
        : [],
    [publicDebt, populationTotal, salaireJournalierMoyen]
  );

  // Slide 3 — treemap of aggregated budget lines
  const treemapData = useMemo(
    () =>
      (budgetLines || [])
        .slice(0, 15)
        .map((bl) => ({ name: bl.sector || "—", size: bl.amount || 0 })),
    [budgetLines]
  );

  // Slide 4 — sector radar (investi vs restitué)
  const radarData = useMemo(() => {
    const sectorTotals = aggregateBudgetIntoSectors(budgetLines);
    const pop = debt.population_active || 1_000_000;
    return SECTOR_ORDER.map((s) => {
      const perCapita = (sectorTotals[s] || 0) / pop;
      const invested = salaireJournalierMoyen
        ? perCapita / salaireJournalierMoyen
        : 0;
      const restituted = invested * (SECTOR_RESTITUTION_DEFAULTS[s] || 1);
      return { sector: s, invested, restituted };
    });
  }, [budgetLines, salaireJournalierMoyen, debt.population_active]);

  // Slide 2 — fiscal life calendar (2 colors)
  const fiscalDays = Math.min(
    Math.round(ind.jours_vie_sacrifies_par_an || 0),
    365
  );

  const slides = useMemo(
    () => [
      {
        id: "title",
        render: () => (
          <SlideShell
            idx={0}
            total={SLIDE_COUNT}
            title={`${project?.country || "—"} · Exercice ${project?.fiscal_year || "—"}`}
            subtitle={`${project?.name || ""} — analyse vitaliste synthétique`}
            testId="presentation-slide-1"
          >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              <BigKpi
                label="Jours/an sacrifiés"
                value={fmtDays(ind.jours_vie_sacrifies_par_an)}
                accent="#C0392B"
              />
              <BigKpi
                label="Années carrière"
                value={fmtYears(ind.annees_vie_sur_carriere)}
                accent="#C9A84C"
              />
              <BigKpi
                label="% vie fiscale"
                value={fmtPct(ind.ratio_vie_fiscale_pct)}
                accent="#F0C987"
              />
              <BigKpi
                label="Total prélèvements/an"
                value={fmtMoney(ind.total_prelevements, currency)}
                accent="#1A3C5E"
              />
              <BigKpi
                label="Dette / contribuable"
                value={fmtMoney(debt.dette_par_contribuable, currency)}
                accent="#6D28D9"
              />
              <BigKpi
                label="Années dette hypothéquées"
                value={fmtYears(debt.annees_vie_hypothequees)}
                accent="#0F766E"
              />
            </div>
          </SlideShell>
        ),
      },
      {
        id: "calendar",
        render: () => (
          <SlideShell
            idx={1}
            total={SLIDE_COUNT}
            title="Calendrier de vie fiscale"
            subtitle={`Un contribuable sacrifie ${fmtDays(
              ind.jours_vie_sacrifies_par_an
            )} sur les 365 jours de l'année.`}
            testId="presentation-slide-2"
          >
            <div className="flex h-full items-center justify-center">
              <svg
                viewBox="0 0 540 520"
                className="h-full max-h-[60vh] w-auto"
                aria-hidden="true"
              >
                {Array.from({ length: 365 }).map((_, i) => {
                  const cols = 20;
                  const x = (i % cols) * 26 + 10;
                  const y = Math.floor(i / cols) * 26 + 10;
                  const state = i < fiscalDays;
                  return (
                    <rect
                      key={i}
                      x={x}
                      y={y}
                      width={20}
                      height={20}
                      rx={3}
                      fill={state ? "#C0392B" : "#1F7A3E"}
                    />
                  );
                })}
              </svg>
              <div className="ml-10 max-w-xs space-y-4 text-sm">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-4 w-4 rounded"
                    style={{ backgroundColor: "#C0392B" }}
                  />
                  <span className="text-white">
                    {fmtDays(ind.jours_vie_sacrifies_par_an)} consacrés à l'État
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-4 w-4 rounded"
                    style={{ backgroundColor: "#1F7A3E" }}
                  />
                  <span className="text-white">
                    {365 - fiscalDays} jours restitués au contribuable
                  </span>
                </div>
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        id: "budget",
        render: () => (
          <SlideShell
            idx={2}
            total={SLIDE_COUNT}
            title="Budget national en vies humaines"
            subtitle={
              national?.available
                ? `${fmtYears(
                    national.annees_vies_budget
                  )} d'existence cumulées consacrées au budget.`
                : "Répartition des dépenses publiques par poste."
            }
            testId="presentation-slide-3"
          >
            <div style={{ width: "100%", height: "60vh" }}>
              <ResponsiveContainer>
                <Treemap
                  data={treemapData}
                  dataKey="size"
                  nameKey="name"
                  stroke="#0b1a2b"
                  fill="#C9A84C"
                  isAnimationActive={false}
                />
              </ResponsiveContainer>
            </div>
          </SlideShell>
        ),
      },
      {
        id: "radar",
        render: () => (
          <SlideShell
            idx={3}
            total={SLIDE_COUNT}
            title="Retour vitaliste sectoriel"
            subtitle="Valeur-vie investie (bleu) vs restituée (or) par secteur public."
            testId="presentation-slide-4"
          >
            <div style={{ width: "100%", height: "60vh" }}>
              <ResponsiveContainer>
                <RadarChart data={radarData} outerRadius="72%">
                  <PolarGrid stroke="#ffffff40" />
                  <PolarAngleAxis
                    dataKey="sector"
                    tick={{ fill: "#fff", fontSize: 13 }}
                  />
                  <Tooltip
                    formatter={(v) =>
                      Number(v).toLocaleString("fr-FR", {
                        maximumFractionDigits: 2,
                      })
                    }
                  />
                  <Legend />
                  <Radar
                    name="Investie"
                    dataKey="invested"
                    stroke="#1A3C5E"
                    fill="#1A3C5E"
                    fillOpacity={0.4}
                  />
                  <Radar
                    name="Restituée"
                    dataKey="restituted"
                    stroke="#C9A84C"
                    fill="#C9A84C"
                    fillOpacity={0.4}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </SlideShell>
        ),
      },
      {
        id: "intergen",
        render: () => (
          <SlideShell
            idx={4}
            total={SLIDE_COUNT}
            title="Dette intergénérationnelle"
            subtitle={`Un enfant né en ${new Date().getFullYear()} hérite déjà de l'hypothèque vitaliste ci-dessous.`}
            testId="presentation-slide-5"
          >
            {mortgageTimeline.length ? (
              <div style={{ width: "100%", height: "60vh" }}>
                <ResponsiveContainer>
                  <LineChart data={mortgageTimeline}>
                    <XAxis
                      dataKey="year_ahead"
                      tick={{ fill: "#fff", fontSize: 12 }}
                      label={{
                        value: "Années après la naissance",
                        position: "insideBottomRight",
                        fill: "#fff",
                      }}
                    />
                    <YAxis
                      tick={{ fill: "#fff", fontSize: 12 }}
                      label={{
                        value: "Jours hypothéqués",
                        angle: -90,
                        position: "insideLeft",
                        fill: "#fff",
                      }}
                    />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="days_of_life"
                      stroke="#C0392B"
                      strokeWidth={3}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center text-center text-white/60">
                Données de dette indisponibles pour cette analyse.
              </div>
            )}
          </SlideShell>
        ),
      },
      {
        id: "infractions",
        render: () => (
          <SlideShell
            idx={5}
            total={SLIDE_COUNT}
            title="Infractions identifiées"
            subtitle={
              legal?.infractions?.length
                ? `${legal.infractions.length} qualifications potentielles retenues par l'analyse juridique.`
                : "Aucune analyse juridique n'a été générée pour ce projet."
            }
            testId="presentation-slide-6"
          >
            {legal?.infractions?.length ? (
              <div style={{ width: "100%", height: "60vh" }}>
                <ResponsiveContainer>
                  <BarChart
                    data={legal.infractions.map((inf) => ({
                      name: inf.qualification,
                      jours: inf.jours_de_vie || 0,
                      niveau: inf.niveau_gravite || 1,
                    }))}
                    layout="vertical"
                    margin={{ left: 60 }}
                  >
                    <XAxis type="number" tick={{ fill: "#fff", fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fill: "#fff", fontSize: 11 }}
                      width={180}
                    />
                    <Tooltip />
                    <Bar dataKey="jours" fill="#C0392B" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center text-center text-white/60">
                Générez une analyse juridique (étape 5) pour peupler cette
                diapositive.
              </div>
            )}
          </SlideShell>
        ),
      },
      {
        id: "conclusion",
        render: () => (
          <SlideShell
            idx={6}
            total={SLIDE_COUNT}
            title="« La vie humaine comme unité de compte. »"
            subtitle=""
            testId="presentation-slide-7"
          >
            <div className="flex h-full flex-col items-start justify-center gap-8 max-w-4xl">
              <blockquote className="font-judicial text-xl italic leading-relaxed text-white/90 sm:text-3xl">
                « L'unité de compte des finances publiques n'est pas la
                monnaie, mais la vie humaine. Chaque prélèvement est une
                tranche d'existence arrachée au contribuable ; chaque dette
                publique, une hypothèque sur la vie future. »
              </blockquote>
              <div className="text-brand-gold">
                — Ahmed ELY Mustapha,
                <br />
                <span className="italic">
                  La Nouvelle Théorie des Finances Publiques — Les Finances
                  Publiques sont des Tranches de Vies
                </span>
              </div>
              <div className="mt-4 text-xs italic text-white/50">
                VITAE-PUBLICA™ · Document pédagogique, aucune valeur
                juridictionnelle.
              </div>
            </div>
          </SlideShell>
        ),
      },
    ],
    [
      project,
      ind,
      currency,
      debt,
      national,
      treemapData,
      radarData,
      mortgageTimeline,
      legal,
      fiscalDays,
    ]
  );

  const goTo = useCallback(
    (next) => {
      setSlide((prev) => Math.max(0, Math.min(SLIDE_COUNT - 1, next)))
    },
    []
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      } else if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        setSlide((prev) => Math.min(SLIDE_COUNT - 1, prev + 1));
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        setSlide((prev) => Math.max(0, prev - 1));
      } else if (e.key === "Home") {
        e.preventDefault();
        setSlide(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setSlide(SLIDE_COUNT - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Fullscreen API (best-effort; some browsers require user gesture)
  useEffect(() => {
    const el = document.documentElement;
    if (el.requestFullscreen && !document.fullscreenElement) {
      el.requestFullscreen().catch(() => {});
    }
    return () => {
      if (document.exitFullscreen && document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  const current = slides[slide];

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0b1a2b] text-white"
      data-testid="presentation-root"
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
    >
      {/* Top-right controls */}
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <div className="hidden items-center gap-1.5 rounded-md border border-white/20 bg-white/5 px-2.5 py-1 text-[11px] text-white/70 sm:flex">
          <Keyboard className="h-3 w-3" />
          ←/→ pour naviguer · Esc pour quitter
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-white/20 bg-white/5 p-2 text-white/90 transition-colors hover:bg-white/15"
          aria-label="Quitter le mode présentation"
          data-testid="presentation-close-btn"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Slide content */}
      <div className="absolute inset-0">{current.render()}</div>

      {/* Navigation */}
      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2">
        <button
          type="button"
          onClick={() => goTo(slide - 1)}
          disabled={slide === 0}
          className="rounded-md border border-white/20 bg-white/5 p-2 text-white/90 transition-opacity disabled:opacity-30"
          aria-label="Slide précédente"
          data-testid="presentation-prev-btn"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="flex gap-1.5">
          {slides.map((s, i) => (
            <button
              type="button"
              key={s.id}
              onClick={() => goTo(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === slide ? "w-8 bg-brand-gold" : "w-4 bg-white/30"
              }`}
              aria-label={`Aller à la slide ${i + 1}`}
              data-testid={`presentation-dot-${i}`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => goTo(slide + 1)}
          disabled={slide === SLIDE_COUNT - 1}
          className="rounded-md border border-white/20 bg-white/5 p-2 text-white/90 transition-opacity disabled:opacity-30"
          aria-label="Slide suivante"
          data-testid="presentation-next-btn"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
